import PropTypes from "prop-types";
import { Row, Col, Table, Button } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { formatDate } from "../../../../utils/date-utils/date-utils";
import { useDownloadPdf } from "../../../../api/service-worksheet-api";
import { useDownloadImage } from "../../../../api/service-history-api";
import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import { createErrorFeedback } from "../../../../context/ui-feedback/action";

export const ServiceHistoryDetail = ({ detail }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const downloadPdf = useDownloadPdf();
  const downloadImage = useDownloadImage();

  const { uiFeedbackDispatch } = useUiFeedback();

  const DataRow = ({ label, value, action }) => (
    <Row>
      <Col sm="6" md="4" lg="3">
        {label ? `${label}:` : null}
      </Col>
      <Col sm="6" md="8" lg="9">
        {action ? (
          <Button variant="link" onClick={action} className="p-0">
            {value}
          </Button>
        ) : (
          <b>{value}</b>
        )}
      </Col>
    </Row>
  );

  const download = async (serviceWorksheetId, pdfName, shortVersion) => {
    try {
      await downloadPdf(serviceWorksheetId, pdfName, shortVersion, (data) => {
        if (data instanceof Error) {
          throw data;
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadpdf.title",
          }),
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadpdf.description",
          }),
        );
      }
    }
  };

  const downloadStepImage = async (path, name) => {
    try {
      await downloadImage(path, name, (data) => {
        if (data instanceof Error) {
          throw data;
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadimage.title",
          }),
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadimage.description",
          }),
        );
      }
    }
  };

  const addString = (text, value, separator) => {
    if (!value || value.length === 0) {
      return text;
    }
    return text + (text.length > 0 ? separator : "") + value;
  };

  const createShipData = (ship) => {
    let response = ship.name;
    let address = addString("", ship.postCode, "");
    address = addString(address, ship.city, ", ");
    address = addString(address, ship.addressLine1, " ");
    address = addString(address, ship.addressLine2, " ");
    return response + (address.length > 0 ? ` (${address})` : "");
  };

  const createUserData = (user) => {
    return `${user.name} (${user.email})`;
  };

  const createUsersData = (users) => {
    return users.map((user) => createUserData(user)).join(", ");
  };

  const MachineDetails = ({ machineDetail, note }) => {
    if (!machineDetail) {
      return null;
    }
    return (
      <>
        <DataRow
          label={intl.formatMessage({
            id: "data.servicehistory.detail.machineNumber",
          })}
          value={machineDetail.machineNumber}
        />
        <DataRow
          label={intl.formatMessage({
            id: "data.servicehistory.detail.machineType",
          })}
          value={machineDetail.machineType}
        />
        <DataRow
          label={intl.formatMessage({
            id: "data.servicehistory.detail.machineOperatingHours",
          })}
          value={machineDetail.machineOperatingHours}
        />
        {note ? (
          <>
            <DataRow
              label={intl.formatMessage({
                id: "data.servicehistory.detail.note",
              })}
              value={note}
            />
          </>
        ) : null}
      </>
    );
  };

  const TableBody = ({ items }) => {
    if (items.length === 0) {
      return null;
    }

    return items.map((item, index) => (
      <tr key={item.id}>
        <td className="col-rownumber">{index + 1}.</td>
        <td className="col-item-number">{item.item.lineObjectNumber}</td>
        <td className="col-item-description">
          {item.item.description} {item.item.description2}
        </td>
        <td className="col-item-quantity">
          {item.item.quantity}/{item.usedQuantity} {item.item.unitOfMeasureCode}
        </td>
      </tr>
    ));
  };

  const ItemsTable = ({ items }) => (
    <>
      <div className="template-table-component table-responsive">
        <Table striped bordered>
          <thead>
            <tr>
              <th className="col-rownumber">#</th>
              <th className="col-item-number">
                <FormattedMessage id="data.serviceworksheet.form.salesorder.item.number" />
              </th>
              <th className="col-item-description">
                <FormattedMessage id="data.serviceworksheet.form.salesorder.item.description" />
              </th>
              <th className="col-item-quantity">
                <FormattedMessage id="data.serviceworksheet.form.salesorder.item.usedquantity" />
              </th>
            </tr>
          </thead>
          <tbody>
            <TableBody items={items} />
          </tbody>
        </Table>
      </div>
      <hr />
    </>
  );

  const Steps = ({ steps }) => (
    <>
      {steps.map((step, index) => (
        <div key={index} className="mt-2">
          <div className="mb-1">
            <b>
              {index + 1}. {step.title}
            </b>
          </div>
          {step.text ? <div>{step.text}</div> : null}
          {step.photos ? (
            <ul>
              {step.photos.map((photo, index) => {
                const photosPath = photo.split("/");
                const name = photosPath[photosPath.length - 1];

                return (
                  <li key={index}>
                    <Button
                      variant="link"
                      onClick={() => {
                        downloadStepImage(photo, name);
                      }}
                      className="p-0"
                    >
                      {name}
                    </Button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ))}
    </>
  );

  return (
    <>
      <DataRow
        label={intl.formatMessage({
          id: "data.servicehistory.detail.serviceWorksheetNo",
        })}
        value={detail.serviceDetail.serviceWorksheetNo}
      />
      <DataRow
        label={intl.formatMessage({ id: "data.servicehistory.detail.pdf" })}
        value={detail.serviceDetail.pdf.name}
        action={() => {
          download(
            detail.serviceDetail.pdf.swId,
            detail.serviceDetail.pdf.name,
            false,
          );
        }}
      />
      {detail.serviceDetail.shortPdf ? (
        <DataRow
          label={null}
          value={detail.serviceDetail.shortPdf.name}
          action={() => {
            download(
              detail.serviceDetail.shortPdf.swId,
              detail.serviceDetail.shortPdf.name,
              true,
            );
          }}
        />
      ) : null}
      <DataRow
        label={intl.formatMessage({
          id: "data.servicehistory.detail.clientName",
        })}
        value={detail.serviceDetail.clientName}
      />
      <DataRow
        label={intl.formatMessage({
          id: "data.servicehistory.detail.salesOrderNo",
        })}
        value={detail.serviceDetail.salesOrderNo}
      />
      <DataRow
        label={intl.formatMessage({
          id: "data.servicehistory.detail.workPlace",
        })}
        value={createShipData(detail.serviceDetail.workPlace)}
      />
      <DataRow
        label={intl.formatMessage({
          id: "data.servicehistory.detail.workDate",
        })}
        value={`${formatDate(new Date(detail.serviceDetail.workDate.startLong), "YYYY.MM.DD. hh:mm")} - ${formatDate(new Date(detail.serviceDetail.workDate.endLong), "YYYY.MM.DD. hh:mm")}`}
      />
      <DataRow
        label={intl.formatMessage({
          id: "data.servicehistory.detail.primaryWorker",
        })}
        value={createUserData(detail.serviceDetail.primaryWorker)}
      />
      <DataRow
        label={intl.formatMessage({ id: "data.servicehistory.detail.workers" })}
        value={createUsersData(detail.serviceDetail.workers)}
      />
      <hr />
      <ItemsTable items={detail.items} />
      {detail.values.map((template, index) => (
        <div key={index}>
          <h5>{template.workName}</h5>
          <div className="ps-4">
            <MachineDetails
              machineDetail={template.machineDetail}
              note={template.note}
            />
            <Steps steps={template.steps} />
          </div>
        </div>
      ))}
    </>
  );
};

ServiceHistoryDetail.propTypes = {
  detail: PropTypes.object.isRequired,
};
